<template>
  <div class="data-map-editor">
    <!-- 显示模式：展示已有映射 -->
    <div v-if="!editing" class="map-display">
      <span v-if="mappings.length === 0" class="empty-hint">暂无映射</span>
      <div v-for="(m, i) in mappings" :key="i" class="map-item">
        <span class="map-src">{{ m.sourceField }}</span>
        <i class="fas fa-arrow-right map-arrow"></i>
        <span class="map-tgt">{{ m.targetTable }}.{{ m.targetField }}</span>
        <button class="map-del" @click.stop="removeMapping(i)" title="删除"><i class="fas fa-times"></i></button>
      </div>
      <button class="map-add-btn" @click="startEditing"><i class="fas fa-plus"></i> 新增映射</button>
    </div>

    <!-- 编辑模式：弹窗式 -->
    <div v-else class="map-modal-overlay">
      <div class="map-modal">
        <div class="map-modal-header">
          <span class="map-modal-title">字段映射</span>
          <button class="map-modal-close" @click="cancelEditing">&times;</button>
        </div>
        <div class="map-modal-body">
          <table class="map-table">
            <thead>
              <tr>
                <th class="map-th-src" colspan="2">源</th>
                <th class="map-th-tgt" colspan="2">目标</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(m, i) in editMappings" :key="i">
                <td class="map-td-check">
                  <input type="checkbox" v-model="m._selected" />
                </td>
                <td class="map-td-src">
                  <select v-model="m.sourceField" class="map-select">
                    <option value="">-- 源字段 --</option>
                    <option v-for="c in sourceColumns" :key="c" :value="c">{{ c }}</option>
                  </select>
                </td>
                <td class="map-td-tgt1">
                  <select v-model="m.targetTable" @change="onTargetTableChange(i)" class="map-select">
                    <option value="">-- 目标表 --</option>
                    <option v-for="t in targetTables" :key="t" :value="t">{{ t }}</option>
                  </select>
                </td>
                <td class="map-td-tgt2">
                  <select v-model="m.targetField" class="map-select">
                    <option value="">-- 目标字段 --</option>
                    <option v-for="c in getTargetColumns(i)" :key="c" :value="c">{{ c }}</option>
                  </select>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="map-modal-footer">
          <button class="map-btn" @click="addEditRow">新增</button>
          <button class="map-btn map-btn-del" @click="removeSelected">删除</button>
          <button class="map-btn map-btn-primary" @click="saveMapping">确定</button>
          <button class="map-btn" @click="cancelEditing">取消</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { getDatabaseTables, getTableColumns } from '../api/process'

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  sourceColumns: { type: Array, default: () => [] },
  targetTables: { type: Array, default: () => [] }
})
const emit = defineEmits(['update:modelValue'])

const editing = ref(false)
const editMappings = ref([])
const targetColumnCache = ref({})

const mappings = computed(() => props.modelValue || [])

async function loadTargetColumns(tableName) {
  if (!tableName) return []
  if (targetColumnCache.value[tableName]) return targetColumnCache.value[tableName]
  try {
    const res = await getTableColumns(tableName)
    const list = res.data || res.Data || res || []
    const cols = Array.isArray(list) ? list.map(c => c.columnName || c.ColumnName || c.name || c.column_name || '') : []
    targetColumnCache.value[tableName] = cols
    return cols
  } catch (e) {
    return []
  }
}

function getTargetColumns(index) {
  const tableName = editMappings.value[index]?.targetTable
  if (!tableName) return []
  return targetColumnCache.value[tableName] || []
}

async function onTargetTableChange(index) {
  const tableName = editMappings.value[index].targetTable
  if (tableName) {
    await loadTargetColumns(tableName)
    editMappings.value[index].targetField = ''
  }
}

function startEditing() {
  editMappings.value = mappings.value.length > 0
    ? mappings.value.map(m => ({ ...m, _selected: false }))
    : [{ sourceField: '', targetTable: '', targetField: '', _selected: false }]
  editing.value = true
}

function cancelEditing() {
  editing.value = false
  targetColumnCache.value = {}
}

function addEditRow() {
  editMappings.value.push({ sourceField: '', targetTable: '', targetField: '', _selected: false })
}

function removeSelected() {
  editMappings.value = editMappings.value.filter(m => !m._selected)
}

function saveMapping() {
  const result = editMappings.value
    .filter(m => m.sourceField && m.targetTable && m.targetField)
    .map(m => ({ sourceField: m.sourceField, targetTable: m.targetTable, targetField: m.targetField }))
  emit('update:modelValue', result)
  editing.value = false
  targetColumnCache.value = {}
}

function removeMapping(index) {
  const result = [...mappings.value]
  result.splice(index, 1)
  emit('update:modelValue', result)
}
</script>

<style scoped>
.data-map-editor {
  border: 1px solid #e5e6eb;
  border-radius: 4px;
  padding: 8px;
  background: #fafbfc;
  min-height: 50px;
}
.map-display {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.empty-hint {
  color: #999;
  font-size: 12px;
  font-style: italic;
}
.map-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  padding: 4px 6px;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 3px;
}
.map-src {
  color: #1a73e8;
  font-weight: 500;
  min-width: 60px;
}
.map-arrow {
  color: #999;
  font-size: 10px;
}
.map-tgt {
  color: #333;
  flex: 1;
}
.map-del {
  background: none;
  border: none;
  color: #f56c6c;
  cursor: pointer;
  padding: 2px 4px;
  font-size: 11px;
}
.map-del:hover { color: #c45656; }
.map-add-btn {
  background: #1a73e8;
  color: #fff;
  border: none;
  padding: 4px 12px;
  border-radius: 3px;
  font-size: 12px;
  cursor: pointer;
  align-self: flex-start;
  margin-top: 4px;
}
.map-add-btn:hover { background: #1557b0; }

/* 弹窗遮罩 */
.map-modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.map-modal {
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.18);
  width: 420px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}
.map-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
}
.map-modal-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}
.map-modal-close {
  background: none;
  border: none;
  font-size: 20px;
  color: #999;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}
.map-modal-close:hover { color: #333; }
.map-modal-body {
  padding: 12px 16px;
  flex: 1;
  overflow-y: auto;
}
.map-table {
  width: 100%;
  border-collapse: collapse;
}
.map-th-src, .map-th-tgt {
  text-align: left;
  padding: 6px 8px;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #ddd;
}
.map-th-tgt {
  text-align: left;
}
.map-td-check {
  width: 28px;
  padding: 4px 2px;
}
.map-td-check input[type="checkbox"] {
  cursor: pointer;
}
.map-td-src, .map-td-tgt1, .map-td-tgt2 {
  padding: 4px 4px;
}
.map-select {
  width: 100%;
  padding: 4px 6px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 12px;
  background: #fff;
  box-sizing: border-box;
}
.map-select:focus {
  border-color: #1a73e8;
  outline: none;
}
.map-modal-footer {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #eee;
  justify-content: center;
}
.map-btn {
  background: #f0f2f5;
  color: #333;
  border: 1px solid #ddd;
  padding: 5px 16px;
  border-radius: 3px;
  font-size: 12px;
  cursor: pointer;
}
.map-btn:hover { background: #e5e7eb; }
.map-btn-del { color: #f56c6c; border-color: #fde2e2; }
.map-btn-del:hover { background: #fef0f0; }
.map-btn-primary {
  background: #1a73e8;
  color: #fff;
  border-color: #1a73e8;
}
.map-btn-primary:hover { background: #1557b0; }
</style>
